import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  ArrowLeft,
  CheckCircle,
  Upload,
  Copy,
  Check,
  ClipboardCopy,
  XCircle
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

type Product = {
  id: string;
  name: string;
  description: string;
  stock: number;
  price: number;
  image_url: string;
  category: "makanan" | "minuman" | "merchandise" | "kerajinan";
  created_at: string;
  updated_at: string;
  created_by: string;
  store_id: string;
  is_active: boolean;
};

type OrderForm = {
  buyer_name: string;
  buyer_address: string;
  buyer_employee_number: string;
  buyer_phone: string;
  payment_method: "transfer" | "cash";
  payment_proof_file?: File;
};

const fetchProduct = async (id: string): Promise<Product> => {
  const { data, error } = await supabase
    .from("products")
    .select(
      "id, name, description, price, stock, image_url, category, created_at, updated_at, created_by, store_id, is_active"
    )
    .eq("id", id)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) throw new Error("Product not found");
  return data as Product;
};

const BuyProduct = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [orderCode, setOrderCode] = useState<string>("");
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderForm, setOrderForm] = useState<OrderForm>({
    buyer_name: "",
    buyer_address: "",
    buyer_employee_number: "",
    buyer_phone: "",
    payment_method: "cash",
  });

  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["product", id],
    queryFn: () => fetchProduct(id!),
    enabled: !!id,
  });

  const handleOrderFormSubmit = async () => {
    if (!product) return;

    // Validasi stock
    if (quantity > product.stock) {
      toast({
        title: "Insufficient Stock",
        description: `Only ${product.stock} units available`,
        variant: "destructive",
      });
      return;
    }

    if (product.stock === 0) {
      toast({
        title: "Out of Stock",
        description: "This product is currently out of stock",
        variant: "destructive",
      });
      return;
    }

    if (
      !orderForm.buyer_name ||
      !orderForm.buyer_address ||
      !orderForm.buyer_phone
    ) {
      toast({
        title: "Form Incomplete",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    if (
      orderForm.payment_method === "transfer" &&
      !orderForm.payment_proof_file
    ) {
      toast({
        title: "Payment Proof Required",
        description: "Please upload payment proof for transfer payments",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let payment_proof_url = null;

      // Upload payment proof if transfer method
      if (
        orderForm.payment_method === "transfer" &&
        orderForm.payment_proof_file
      ) {
        const fileExt = orderForm.payment_proof_file.name.split(".").pop();
        const fileName = `payment-proof-${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("product-images")
          .upload(fileName, orderForm.payment_proof_file);

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from("product-images").getPublicUrl(fileName);

        payment_proof_url = publicUrl;
      }

      // Insert into orders table (order_code is auto-generated by trigger)
      const { data: orderData, error } = await supabase
        .from("orders")
        .insert({
          product_id: product.id,
          buyer_name: orderForm.buyer_name,
          buyer_address: orderForm.buyer_address,
          buyer_employee_number: orderForm.buyer_employee_number || null,
          buyer_phone: orderForm.buyer_phone,
          quantity,
          total_price: product.price * quantity,
          payment_method: orderForm.payment_method,
          payment_proof_url,
          status: "pending",
        } as any)
        .select("*")
        .single();

      if (error) throw error;

      // Update stock produk setelah order berhasil menggunakan function yang aman
      console.log("Updating stock:", {
        productId: product.id,
        currentStock: product.stock,
        quantity,
      });

      const { error: stockError } = await supabase.rpc(
        "update_product_stock_on_purchase",
        {
          product_id_param: product.id,
          quantity_to_subtract: quantity,
        }
      );

      if (stockError) {
        console.error("Failed to update stock:", stockError);
        toast({
          title: "Warning",
          description:
            "Order successful but stock update failed. Please contact admin.",
          variant: "destructive",
        });
      } else {
        console.log("Stock updated successfully");
      }

      setOrderCode(orderData.order_code);
      setPurchaseSuccess(true);
    } catch (err: any) {
      toast({
        title: "Order Failed",
        description: err.message || "There was an error processing your order",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Copy with tooltip component
  // CopyWithTooltip.tsx
  const CopyWithTooltip = ({ textToCopy }: { textToCopy: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // reset after 1.5s
    };

    return (
      <div className="relative flex items-center group">
        {/* Tooltip */}
        <div
          className={`
          absolute -top-7 right-0 text-xs px-2 py-1 rounded bg-black text-white
          transition-opacity duration-200
          group-hover:opacity-100
          ${copied ? "opacity-100" : "opacity-0"}
        `}
        >
          {copied ? "Copied!" : "Copy"}
        </div>

        {/* Copy Button */}
        <button
          onClick={handleCopy}
          className="p-1 hover:text-blue-600 transition-colors"
          type="button"
        >
          {copied ? <Check size={18} /> : <Copy size={18} />}
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin h-10 w-10 text-primary mb-4" />
        <p className="text-lg">Loading product details...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <div className="bg-red-50 p-6 rounded-xl mb-6">
          <h2 className="text-2xl font-bold text-red-600 mb-2">
            Product Not Found
          </h2>
          <p className="text-red-500">
            The product you're looking for doesn't exist or may have been
            removed.
          </p>
        </div>
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </Button>
      </div>
    );
  }

  //  Cek Produk Aktif
  if (!product.is_active) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
              <XCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <CardTitle className="text-xl font-semibold text-yellow-700">
              Produk Tidak Tersedia
            </CardTitle>
            <CardDescription className="text-yellow-600">
              Maaf, produk ini sedang tidak aktif untuk sementara waktu.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Kembali ke Katalog
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (purchaseSuccess) {
    return (
      <div className="max-w-md mx-auto p-8 bg-card rounded-xl shadow-sm text-center">
        <div className="bg-green-50 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Order Confirmed!</h2>
        <p className="text-muted-foreground mb-4">
          You've successfully ordered {quantity}{" "}
          {quantity > 1 ? "items" : "item"} of {product.name}
        </p>
        <div className="bg-muted p-4 rounded-lg mb-3">
          <p className="font-medium mb-2">Order Details</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Order Code:</span>
              <span className="font-bold text-primary">
                <div className="flex items-center justify-between space-x-2">
                  {orderCode}
                  <CopyWithTooltip textToCopy={orderCode} />
                </div>
              </span>
            </div>
            <div className="flex justify-between">
              <span>Payment Method:</span>
              <span className="capitalize">{orderForm.payment_method}</span>
            </div>
            <div className="flex justify-between">
              <span>Total:</span>
              <span className="font-bold">
                Rp {Number(product.price * quantity).toLocaleString("id-ID")}
              </span>
            </div>
          </div>
        </div>
        <p className="text-gray-500 mb-4 text-sm text-primary font-bold">
          Silahkan salin Order Code untuk melacak status pesanan Anda.
        </p>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="flex-1"
          >
            Back to Catalog
          </Button>
        </div>
      </div>
    );
  }

  const totalPrice = product.price * quantity;

  if (showOrderForm) {
    return (
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <Button
          onClick={() => setShowOrderForm(false)}
          variant="ghost"
          className="mb-6 gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Product
        </Button>

        <div className="bg-card rounded-xl p-6 shadow-sm">
          <h2 className="text-2xl font-bold mb-6">Complete Your Order</h2>

          <div className="grid gap-6">
            <div className="grid gap-4">
              <div>
                <Label htmlFor="buyer_name">Full Name *</Label>
                <Input
                  id="buyer_name"
                  value={orderForm.buyer_name}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      buyer_name: e.target.value,
                    }))
                  }
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <Label htmlFor="buyer_address">Address *</Label>
                <Textarea
                  id="buyer_address"
                  value={orderForm.buyer_address}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      buyer_address: e.target.value,
                    }))
                  }
                  placeholder="Enter your full address"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="buyer_employee_number">
                  Employee Number (NOPEG)
                </Label>
                <Input
                  id="buyer_employee_number"
                  value={orderForm.buyer_employee_number}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      buyer_employee_number: e.target.value,
                    }))
                  }
                  placeholder="Enter your employee number"
                />
              </div>

              <div>
                <Label htmlFor="buyer_phone">Phone/WhatsApp *</Label>
                <Input
                  id="buyer_phone"
                  value={orderForm.buyer_phone}
                  onChange={(e) =>
                    setOrderForm((prev) => ({
                      ...prev,
                      buyer_phone: e.target.value,
                    }))
                  }
                  placeholder="Enter your phone/WhatsApp number"
                />
              </div>

              <div>
                <Label htmlFor="payment_method">Payment Method *</Label>
                <Select
                  value={orderForm.payment_method}
                  onValueChange={(value: "transfer" | "cash") =>
                    setOrderForm((prev) => ({ ...prev, payment_method: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {orderForm.payment_method === "transfer" && (
                <div>
                  <Label>Transfer To</Label>
                  <div className="mt-2">
                    <div className="flex items-center justify-between space-x-2">
                      <Input
                        value="CIMB Niaga : 762922085900 AGUS PURWANTO"
                        disabled
                        readOnly
                        className="flex-1"
                      />
                      <CopyWithTooltip textToCopy="762922085900" />
                    </div>
                  </div>
                </div>
              )}

              {orderForm.payment_method === "transfer" && (
                <div>
                  <Label htmlFor="payment_proof">Upload Payment Proof *</Label>
                  <div className="mt-2">
                    <Input
                      id="payment_proof"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setOrderForm((prev) => ({
                            ...prev,
                            payment_proof_file: file,
                          }));
                        }
                      }}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Upload screenshot or photo of your transfer receipt
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="bg-muted p-4 rounded-lg">
                <h3 className="font-medium mb-2">Order Summary</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>{product.name}</span>
                    <span>{quantity}x</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Total:</span>
                    <span>Rp {Number(totalPrice).toLocaleString("id-ID")}</span>
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleOrderFormSubmit}
              className="w-full py-6 text-lg font-bold"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing Order...
                </>
              ) : (
                "Confirm Order"
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      <Button
        onClick={() => navigate("/")}
        variant="ghost"
        className="mb-6 gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Katalog
      </Button>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Product Image Section */}
        <div className="bg-muted rounded-xl overflow-hidden">
          <div className="aspect-square bg-gradient-to-br from-muted to-muted/80 flex items-center justify-center">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="object-cover w-full h-full"
              />
            ) : (
              <span className="text-muted-foreground">No Image</span>
            )}
          </div>
        </div>

        {/* Product Details Section */}
        {/* Product Details Section */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
          <p className="text-muted-foreground mb-6">{product.description}</p>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl font-bold text-primary">
                Rp {Number(product.price).toLocaleString("id-ID")}
              </span>
            </div>

            {/* Stock Info */}
            <div className="mb-4 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Stock tersedia:</span>
                <span
                  className={`font-bold ${product.stock > 10
                    ? "text-green-600"
                    : product.stock > 0
                      ? "text-yellow-600"
                      : "text-red-600"
                    }`}
                >
                  {product.stock} unit
                </span>
              </div>
              {product.stock <= 10 && product.stock > 0 && (
                <p className="text-xs text-yellow-600 mt-1">Stock terbatas!</p>
              )}
              {product.stock === 0 && (
                <p className="text-xs text-red-600 mt-1">Produk sedang habis</p>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="quantity" className="block mb-2 font-medium">
                  Quantity
                </Label>
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1 || product.stock === 0}
                  >
                    -
                  </Button>
                  <Input
                    id="quantity"
                    type="number"
                    min={1}
                    max={product.stock}
                    value={quantity}
                    onChange={(e) => {
                      const value = Math.max(
                        1,
                        Math.min(product.stock, Number(e.target.value))
                      );
                      setQuantity(isNaN(value) ? 1 : value);
                    }}
                    className="w-20 text-center"
                    disabled={product.stock === 0}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setQuantity((q) => Math.min(product.stock, q + 1))
                    }
                    disabled={quantity >= product.stock || product.stock === 0}
                  >
                    +
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Maksimal {product.stock} unit
                </p>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between mb-2">
                  <span>Subtotal:</span>
                  <span>Rp {Number(totalPrice).toLocaleString("id-ID")}</span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-primary">
                    Rp {Number(totalPrice).toLocaleString("id-ID")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <Button
            onClick={() => setShowOrderForm(true)}
            className="w-full py-6 text-lg font-bold"
            disabled={product.stock === 0 || quantity > product.stock}
          >
            {product.stock === 0 ? "Out of Stock" : "Complete Purchase"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BuyProduct;
